import React, { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ordersListPath } from 'routes/url-constants'

import { selectCurrentUser, selectToken } from 'store/users/users.selectors'
import { selectOrderFilterId } from 'store/orders/orders.selectors'
import { fetchOrdersRequest } from 'store/orders/orders.actions'
import { addAlert } from 'store/alerts/alerts.actions'
import { selectNotifications } from 'store/notifications/notifications.selectors'
import { notificationsUpdateSuccess } from 'store/notifications/notifications.actions'

import { UserRole } from 'types/user'
import { AlertVariant } from 'types/alert'
import { OrderFilterId, ResolutionId } from 'types/order'
import { ORDER_STATUS_CHANGED, Notification } from 'types/notifications'

import { showOrderStatusChangeNotification } from 'utils/browser-notifications'
import type { OrderStatusChangeItem } from 'utils/order-status-notification-text'
import { isNotificationLeaderTab } from 'utils/notification-tab-leader'
import { isNotificationsModuleEnabled } from 'utils/is-notifications-module-enabled'
import { fetchFeNotificationsFromOtk } from 'requests/notifications'

import { FINISH_RESOLUTIONS } from 'components/molecules/OrderSpace/spaces/Header/components/ResolutionForm/constants'
import { useLocation } from 'react-router'

/** Событие для мигания заголовка вкладки (app.tsx). */
export const ORDER_STATUS_CHANGED_EVENT = 'otk-order-status-changed'

/**
 * Для CUSTOMER: стрим событий смены `resolution` и оповещение о статусе на всём приложении
 * (не только на странице списка), чтобы фоновая вкладка тоже получала сигнал.
 *
 * P2 / module flag on: SSE is transport for otk/notifications (`order.status.changed`);
 * toast only from SSE (not from list hydrate) to avoid double-toast.
 */
export const CustomerOrderStatusMonitor: React.FC = () => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)
  const currentUser = useSelector(selectCurrentUser)
  const inbox = useSelector(selectNotifications)
  const inboxRef = useRef(inbox)
  const eventSourceRef = useRef<EventSource | null>(null)
  const debounceTimeoutRef = useRef<number | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)

  const orderFilterId = useSelector(selectOrderFilterId)
  const orderFilterIdRef = useRef(orderFilterId)

  useEffect(() => {
    orderFilterIdRef.current = orderFilterId
  }, [orderFilterId])

  useEffect(() => {
    inboxRef.current = inbox
  }, [inbox])

  const location = useLocation()
  const pathnameRef = useRef(location.pathname)

  useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  const isCustomer = currentUser?.role === UserRole.CUSTOMER
  const active = Boolean(token && isCustomer)
  const moduleEnabled = isNotificationsModuleEnabled()

  const updateOrdersNow = useCallback(() => {
    // Обновляем список только на странице заявок (иначе не трогаем состояние списка в фоне).
    if (pathnameRef.current !== ordersListPath) return
    if (orderFilterIdRef.current !== OrderFilterId.FEED) return

    console.log('fetchOrdersRequest')
    dispatch(fetchOrdersRequest())
  }, [dispatch])

  const scheduleUpdateOrders = useCallback(() => {
    // Debounce: многократные нотификации подряд приводят к одному update после "тишины".
    if (debounceTimeoutRef.current !== null) window.clearTimeout(debounceTimeoutRef.current)

    debounceTimeoutRef.current = window.setTimeout(() => {
      debounceTimeoutRef.current = null
      updateOrdersNow()
    }, 1000)
  }, [updateOrdersNow])

  useEffect(() => {
    if (!active) return

    const serverBase = process.env.REACT_APP_V3_SERVER
    if (!serverBase || !token) return

    // Разово подтянем список для актуального рендера "Ленты".
    updateOrdersNow()

    const url = `${serverBase}/order/status/stream?token=${encodeURIComponent(token)}`

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    const closeEventSource = () => {
      if (eventSourceRef?.current) eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    const upsertStatusIntoInbox = (item: Notification) => {
      const current = inboxRef.current || []
      const withoutSame = current.filter(
        (row: Notification) => !(row.type === ORDER_STATUS_CHANGED && row.orderId === item.orderId && row.resolutionId === item.resolutionId),
      )
      dispatch(notificationsUpdateSuccess([item, ...withoutSame]))
    }

    const onOrderStatus = (event: MessageEvent) => {
      let payload: {
        orderId?: string
        resolutionId?: string | number
        id?: string
        type?: string
      } | null = null
      try {
        payload = JSON.parse(event.data as string)
      } catch {
        payload = null
      }

      if (payload?.orderId && payload?.resolutionId) {
        const isLeaderTab = isNotificationLeaderTab()
        const resolutionId = String(payload.resolutionId)

        const items: OrderStatusChangeItem[] = [
          {
            orderNumber: payload.orderId,
            resolution: resolutionId,
          },
        ]

        // Toast / browser Notification only from SSE push (never from list hydrate).
        if (isLeaderTab) {
          showOrderStatusChangeNotification({ items })
        }
        window.dispatchEvent(
          new CustomEvent(ORDER_STATUS_CHANGED_EVENT, {
            detail: { orderNumbers: [payload.orderId] },
          }),
        )

        const alertHeader = `Изменена заявка ${payload?.orderId}`
        let alertText = ''

        if (resolutionId === ResolutionId.CLARIFY_DATA) alertText = `Уточните данные заявки`
        if (resolutionId === ResolutionId.WIP) alertText = `Заявка в работе`
        if (FINISH_RESOLUTIONS.includes(resolutionId as ResolutionId)) alertText = `Заявка завершена`

        if (alertText) {
          dispatch(
            addAlert({
              lifetime: 1000 * 25,
              header: alertHeader,
              text: alertText,
              variant: AlertVariant.INFO,
              withLineBreaks: true,
            }),
          )
        }

        if (moduleEnabled) {
          upsertStatusIntoInbox({
            id: payload.id,
            type: ORDER_STATUS_CHANGED,
            orderId: payload.orderId,
            isInternal: false,
            createdAt: new Date().toISOString(),
            resolutionId,
          })

          // Soft re-sync SoT from OTK (ids); do not toast again.
          fetchFeNotificationsFromOtk(token)
            .then((list) => dispatch(notificationsUpdateSuccess(list)))
            .catch((err) => console.error('notifications sync after status SSE failed', err))
        }
      }

      scheduleUpdateOrders()
    }

    // eslint `no-use-before-define`: объявляем `connect` до использования в `scheduleReconnect`.
    // При этом сохраняем взаимные вызовы connect <-> scheduleReconnect.
    let connect: () => void = () => {}

    const scheduleReconnect = () => {
      clearReconnectTimer()
      closeEventSource()

      // Равномерное случайное распределение 0…5 мин, чтобы клиенты не долбили сервер одновременно.
      const delayMs = Math.floor(Math.random() * 300_001)

      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect()
      }, delayMs)
    }

    connect = () => {
      clearReconnectTimer()
      closeEventSource()

      const es = new EventSource(url) as any
      eventSourceRef.current = es
      es.addEventListener('order-status', onOrderStatus)

      es.onerror = () => {
        // В некоторых сценариях (не-200 от сервера/прокси) браузер переводит EventSource в CLOSED без автоповтора.
        // Явно переподключаемся со случайной задержкой.
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      clearReconnectTimer()
      closeEventSource()
      if (debounceTimeoutRef.current !== null) {
        window.clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
    }
  }, [active, dispatch, moduleEnabled, scheduleUpdateOrders, token, updateOrdersNow])

  return null
}
